import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, ShieldCheck, HelpCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LeadConsultationForm } from "@/components/LeadConsultationForm";

export const PricingPage: React.FC<{ onOpenConsultation: () => void }> = ({
  onOpenConsultation,
}) => {
  const [searchParams] = useSearchParams();
  const [propertyType, setPropertyType] = useState<
    "residential" | "commercial"
  >("residential");

  useEffect(() => {
    const type = searchParams.get("type");
    if (type === "commercial" || type === "residential") {
      setPropertyType(type);
    }
  }, [searchParams]);

  return (
    <div className="pt-24 pb-20 bg-slate-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
          <span className="text-xs font-extrabold uppercase tracking-widest text-primary bg-primary/10 px-3 py-1 rounded-md">
            Transparent Management Pricing
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            Simple, Honest, Value-Driven Management
          </h1>
          <p className="text-slate-600 dark:text-slate-300 text-sm sm:text-base">
            {propertyType === "residential"
              ? "Transparent rates for single-family homes, condos, townhomes, and small multi-unit residential properties."
              : "Competitive management solutions for office buildings, retail centers, industrial facilities, and commercial portfolios."}
          </p>

          <div className="inline-flex items-center p-1 rounded-xl bg-slate-200 dark:bg-slate-800 mt-4">
            <button
              onClick={() => setPropertyType("residential")}
              className={`px-5 py-2 text-xs font-bold rounded-lg transition-all ${
                propertyType === "residential"
                  ? "bg-white dark:bg-slate-900 text-primary shadow-sm"
                  : "text-slate-600 dark:text-slate-400"
              }`}
            >
              Residential
            </button>
            <button
              onClick={() => setPropertyType("commercial")}
              className={`px-5 py-2 text-xs font-bold rounded-lg transition-all ${
                propertyType === "commercial"
                  ? "bg-white dark:bg-slate-900 text-primary shadow-sm"
                  : "text-slate-600 dark:text-slate-400"
              }`}
            >
              Commercial
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {/* Plan 1 */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col justify-between">
            <div>
              <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                Essential Care
              </div>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                Leasing Only
              </h3>
              <p className="text-xs text-slate-500 mb-6">
                {propertyType === "residential"
                  ? "Ideal for hands-on residential owners who need professional tenant marketing & placement."
                  : "Perfect for commercial landlords who handle operations but need expert tenant acquisition."}
              </p>

              <div className="mb-6 pb-6 border-b border-slate-100">
                <span className="text-4xl font-extrabold text-slate-900 dark:text-white">
                  75%
                </span>
                <span className="text-xs text-slate-500 ml-1">
                  of 1st Month Rent
                </span>
              </div>

              <ul className="space-y-3 text-xs text-slate-700 dark:text-slate-300">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" /> High-impact
                  online marketing
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" /> Professional
                  photo listing
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" /> Complete
                  background & credit screening
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" /> Lease execution
                  & security deposit collection
                </li>
              </ul>
            </div>

            <Button
              onClick={onOpenConsultation}
              variant="outline"
              className="w-full mt-8"
            >
              Select Leasing Option
            </Button>
          </div>

          {/* Plan 2 Featured */}
          <div className="bg-slate-900 text-white rounded-2xl p-8 border-2 border-accent shadow-2xl flex flex-col justify-between relative transform lg:-translate-y-2">
            <span className="absolute -top-3.5 right-6 bg-accent text-slate-950 font-bold text-[10px] uppercase tracking-widest px-3 py-1 rounded-full">
              Most Popular
            </span>
            <div>
              <div className="text-xs font-bold text-accent uppercase tracking-wider mb-2">
                Full Management
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                Comprehensive Management
              </h3>
              <p className="text-xs text-slate-300 mb-6">
                {propertyType === "residential"
                  ? "Complete hands-off peace of mind for residential property owners."
                  : "End-to-end commercial property oversight with dedicated support."}
              </p>

              <div className="mb-6 pb-6 border-b border-slate-800">
                <span className="text-4xl font-extrabold text-white">
                  {propertyType === "residential" ? "8%" : "6%"}
                </span>
                <span className="text-xs text-slate-400 ml-1">
                  Monthly Gross Rent
                </span>
              </div>

              <ul className="space-y-3 text-xs text-slate-200">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-accent" />{" "}
                  <strong>All Leasing Only Features Included</strong>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-accent" /> Automated monthly
                  rent collection
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-accent" /> 24/7 emergency
                  maintenance dispatch
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-accent" /> Real-time online
                  owner portal
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-accent" /> Monthly financial
                  statements & 1099 tax prep
                </li>
              </ul>
            </div>

            <Button
              onClick={onOpenConsultation}
              className="w-full mt-8 bg-accent text-slate-950 font-bold hover:bg-accent/90"
            >
              Get Started Now <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {/* Plan 3 */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col justify-between">
            <div>
              <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                Enterprise
              </div>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                Custom Portfolio
              </h3>
              <p className="text-xs text-slate-500 mb-6">
                {propertyType === "residential"
                  ? "Tailored solutions for multi-family complexes and growing residential portfolios."
                  : "Bespoke management frameworks for large commercial portfolios and mixed-use developments."}
              </p>

              <div className="mb-6 pb-6 border-b border-slate-100">
                <span className="text-3xl font-extrabold text-slate-900 dark:text-white">
                  Custom Quote
                </span>
              </div>

              <ul className="space-y-3 text-xs text-slate-700 dark:text-slate-300">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" /> Dedicated
                  account manager
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" /> On-site
                  staffing options
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" /> Commercial
                  lease compliance
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500" /> Tailored
                  financial integration
                </li>
              </ul>
            </div>

            <Button
              onClick={onOpenConsultation}
              variant="outline"
              className="w-full mt-8"
            >
              Request Custom Quote
            </Button>
          </div>
        </div>

        {/* Form CTA */}
        <div className="max-w-4xl mx-auto">
          <LeadConsultationForm title="Lock In Your Custom Management Rate" />
        </div>
      </div>
    </div>
  );
};
